import { useState } from 'react';
import { Alert, Badge, Box, Button, Heading, Spinner, TextareaInput } from '@hyphen/hyphen-components';

import { api } from '../lib/api.js';
import { useResource } from '../lib/session.js';
import { formatWhen } from './evidence-shared.jsx';

/**
 * The conversation about one control.
 *
 * Oldest first, the order it was said in. Each comment keeps its author and
 * time, because the point of writing it here rather than in the control's notes
 * is that someone said it, and when.
 */
export function Comments({ haiID, canComment }) {
  const { data: comments, error, setData, setError } = useResource(
    `/controls/${encodeURIComponent(haiID)}/comments`, [haiID]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);

  async function add(event) {
    event.preventDefault();
    const body = draft.trim();
    if (!body) return;
    setBusy(true);
    setError(null);
    try {
      const comment = await api.post(`/controls/${encodeURIComponent(haiID)}/comments`, { body });
      // Cleared only once it is stored: a failed post that had already emptied
      // the box would lose what was written.
      setDraft('');
      setData([...(comments ?? []), comment]);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  if (error && !comments) return <Alert variant="warning">{error}</Alert>;
  if (!comments) return <Spinner />;

  return (
    <Box gap="xl">
      <Heading as="h2" size="md">Comments</Heading>

      {error && <Alert variant="warning">{error}</Alert>}

      {comments.length === 0 && (
        <Box color="secondary">
          {canComment ? 'Nothing said about this control yet.' : 'No comments.'}
        </Box>
      )}

      <Box gap="lg">
        {comments.map((comment) => (
          <Comment
            key={comment.id}
            comment={comment}
            onChanged={(updated) => setData(comments.map((c) => (c.id === updated.id ? updated : c)))}
            onRemoved={() => setData(comments.filter((c) => c.id !== comment.id))}
            onError={setError}
          />
        ))}
      </Box>

      {canComment && (
        <Box as="form" gap="sm" onSubmit={add} maxWidth="48rem">
          <TextareaInput
            id={`comment-${haiID}`}
            label="Add a comment"
            name="body"
            rows={3}
            value={draft}
            isDisabled={busy}
            onChange={(event) => setDraft(event.target.value)}
          />
          <Box direction="row">
            <Button type="submit" size="sm" isLoading={busy} isDisabled={!draft.trim()}>
              Comment
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}

function Comment({ comment, onChanged, onRemoved, onError }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.body);
  const [busy, setBusy] = useState(false);

  async function act(fn) {
    setBusy(true);
    onError(null);
    try {
      await fn();
    } catch (err) {
      onError(err.message);
    } finally {
      setBusy(false);
    }
  }

  const save = () =>
    act(async () => {
      const body = draft.trim();
      if (!body) return;
      onChanged(await api.patch(`/comments/${comment.id}`, { body }));
      setEditing(false);
    });

  const remove = () =>
    act(async () => {
      await api.delete(`/comments/${comment.id}`);
      onRemoved();
    });

  return (
    <Box
      borderWidth="sm"
      borderColor="subtle"
      radius="md"
      background="primary"
      maxWidth="48rem"
    >
      <Box
        direction="row"
        alignItems="center"
        gap="sm"
        padding="md lg"
        background="secondary"
        borderWidth="0 0 sm 0"
        borderColor="subtle"
        fontSize="sm"
      >
        <Box as="span" fontWeight="medium">{comment.author}</Box>
        <Box as="span" color="secondary">{formatWhen(comment.created_at)}</Box>
        {comment.edited && <Badge size="sm" variant="outline">edited</Badge>}
        {(comment.mine || comment.can_delete) && !editing && (
          <Box direction="row" gap="sm" style={{ marginLeft: 'auto' }}>
            {comment.mine && (
              <Button variant="tertiary" size="sm" isDisabled={busy} onClick={() => setEditing(true)}>
                Edit
              </Button>
            )}
            <Button variant="tertiary" size="sm" isLoading={busy} onClick={remove}>
              Delete
            </Button>
          </Box>
        )}
      </Box>

      <Box padding="lg">
        {editing ? (
          <Box gap="sm">
            <TextareaInput
              id={`comment-edit-${comment.id}`}
              label="Edit comment"
              hideLabel
              name="body"
              rows={3}
              value={draft}
              isDisabled={busy}
              onChange={(event) => setDraft(event.target.value)}
            />
            <Box direction="row" gap="sm">
              <Button size="sm" isLoading={busy} isDisabled={!draft.trim()} onClick={save}>Save</Button>
              <Button
                size="sm"
                variant="secondary"
                isDisabled={busy}
                onClick={() => {
                  setDraft(comment.body);
                  setEditing(false);
                }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        ) : (
          // Whitespace preserved: what someone typed, including their line
          // breaks, is what the next person reads.
          <Box as="p" style={{ whiteSpace: 'pre-wrap' }}>{comment.body}</Box>
        )}
      </Box>
    </Box>
  );
}
